import { Graphics } from "pixi.js";

import { LayerName } from "../../config/layers";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { createGraphics } from "../../core/entity/GameSprite";
import { V, V2d } from "../../core/Vector";
import { createPolygonSegments } from "./VectorSpriteUtils";

/**
 * Represents a single line segment for rendering
 */
export interface VectorSegment {
  start: V2d;
  end: V2d;
  color: number;
  alpha?: number;
  thickness: number;
}

/**
 * Context passed to line segment filters
 */
export interface SegmentFilterContext {
  deltaTime: number;
  totalTime: number;
  entityPosition: V2d;
  [key: string]: any; // Filter-specific state
}

/**
 * Interface that line segment filters must implement
 */
export interface VectorFilterLike {
  apply(
    segments: VectorSegment[],
    context: SegmentFilterContext,
  ): VectorSegment[];
}

/**
 * Type guard to check if an entity is a line segment filter
 */
export function isVectorFilter(
  entity: Entity | null | undefined,
): entity is Entity & VectorFilterLike {
  return (
    entity != null &&
    typeof entity === "object" &&
    typeof (entity as any).apply === "function"
  );
}

/**
 * A reusable entity for rendering line segments with visual effects
 *
 * LineSegmentRenderer provides a consistent way to render vector graphics with:
 * - Individual line segment definition with per-segment colors/thickness
 * - Composable visual filters for effects
 * - Automatic positioning via entity system
 * - Performance optimized rendering
 *
 * @example
 * ```typescript
 * // Simple cross pattern
 * const cross = LineSegmentRenderer.fromSegments([
 *   { start: V(-10, 0), end: V(10, 0), color: 0xFF0000, thickness: 2 },
 *   { start: V(0, -10), end: V(0, 10), color: 0xFF0000, thickness: 2 }
 * ]);
 *
 * // Add wobble effect as child entity
 * cross.addFilter(new WobbleFilter(3, 2));
 *
 * // Add flash effect and activate it
 * const flashFilter = cross.addFilter(new FlashFilter());
 * flashFilter.activate(); // Uses BaseEntity timing internally
 * ```
 */
export default class VectorSprite extends BaseEntity implements Entity {
  sprite: NonNullable<BaseEntity["sprite"]> & Graphics;
  pausable = false;

  private segments: VectorSegment[] = [];
  private lastRenderedSegmentHash: string = "";

  /**
   * Create a new line segment renderer
   * @param segments Array of line segments to render
   * @param layerName Which rendering layer to place the segments in
   */
  constructor(segments: VectorSegment[] = [], layerName: LayerName = "main") {
    super();

    this.segments = [...segments]; // Copy to avoid mutation
    this.sprite = createGraphics(layerName);
  }

  /**
   * Create a LineSegmentRenderer from a polygon
   * @param points Polygon vertices
   * @param color Color for all segments
   * @param thickness Thickness for all segments
   * @param layerName Which rendering layer to place the segments in
   */
  static fromPolygon(
    points: V2d[],
    color: number,
    thickness: number,
    layerName: LayerName = "main",
  ): VectorSprite {
    const segments = createPolygonSegments(points, color, thickness);
    return new VectorSprite(segments, layerName);
  }

  /**
   * Create a LineSegmentRenderer from line segments
   * @param segments Array of line segments
   * @param layerName Which rendering layer to place the segments in
   */
  static fromSegments(
    segments: VectorSegment[],
    layerName: LayerName = "main",
  ): VectorSprite {
    return new VectorSprite(segments, layerName);
  }

  /**
   * Add a visual filter to this renderer as a child entity
   * The filter will be automatically applied during rendering
   */
  addFilter<T extends BaseEntity & VectorFilterLike>(filter: T): T {
    return this.addChild(filter);
  }

  /**
   * Update the segments to render
   */
  setSegments(segments: VectorSegment[]): void {
    this.segments = [...segments];
  }

  /**
   * Get the current segments (before filter effects)
   */
  getSegments(): VectorSegment[] {
    return [...this.segments];
  }

  /**
   * Set the position of the renderer
   */
  setPosition([x, y]: [number, number]): void {
    if (this.sprite) {
      this.sprite.position.set(x, y);
    }
  }

  /**
   * Set the rotation of the renderer
   * @param angle Rotation angle in radians
   */
  setRotation(angle: number): void {
    if (this.sprite) {
      this.sprite.rotation = angle;
    }
  }

  /**
   * Get the current position of the renderer
   */
  getPosition(): V2d {
    if (this.sprite) {
      return V(this.sprite.position.x, this.sprite.position.y);
    }
    return V(0, 0);
  }

  /**
   * Get the current rotation of the renderer
   */
  getRotation(): number {
    return this.sprite?.rotation || 0;
  }

  /**
   * Set the alpha (transparency) of the renderer
   * @param alpha Alpha value (0 = transparent, 1 = opaque)
   */
  setAlpha(alpha: number): void {
    if (this.sprite) {
      this.sprite.alpha = alpha;
    }
  }

  /**
   * Get the current alpha of the renderer
   */
  getAlpha(): number {
    return this.sprite?.alpha || 1;
  }

  /**
   * Get all vector filter children
   */
  private getVectorFilters(): (Entity & VectorFilterLike)[] {
    return this.children.filter(isVectorFilter);
  }

  /**
   * Calculate final segments after applying all filters
   */
  private getFilteredSegments(dt: number): VectorSegment[] {
    let segments = [...this.segments];

    const context: SegmentFilterContext = {
      deltaTime: dt,
      totalTime: this.game?.elapsedUnpausedTime || 0,
      entityPosition: V(this.getPosition()),
    };

    const filters = this.getVectorFilters();
    for (const filter of filters) {
      segments = filter.apply(segments, context);
    }

    return segments;
  }

  onRender(dt: number): void {
    // Calculate final segments
    const segments = this.getFilteredSegments(dt);

    // Check if segments have changed to avoid unnecessary clear() calls
    const segmentHash = JSON.stringify(segments);
    if (segmentHash !== this.lastRenderedSegmentHash) {
      this.sprite.clear();

      for (const segment of segments) {
        // Special case: if start and end are the same point, render as a circle
        if (
          segment.start.x === segment.end.x &&
          segment.start.y === segment.end.y
        ) {
          this.sprite
            .circle(segment.start.x, segment.start.y, segment.thickness / 2)
            .fill({ color: segment.color, alpha: segment.alpha ?? 1 });
        } else {
          // Normal line segment
          this.sprite
            .moveTo(segment.start.x, segment.start.y)
            .lineTo(segment.end.x, segment.end.y)
            .stroke({
              color: segment.color,
              width: segment.thickness,
              cap: "round",
              join: "round",
              alpha: segment.alpha ?? 1,
            });
        }
      }

      this.lastRenderedSegmentHash = segmentHash;
    }
  }
}
